import { createTheme } from '@mui/material/styles';

export const tokens = {
  fg: '#171717',
  muted: '#737373',
  border: '#e5e5e5',
  soft: '#f5f5f5',
  sidebar: '#fafafa',
  ok: '#166534',
  okSoft: '#dcfce7',
  okBand: '#86efac',
  bad: '#b91c1c',
  badSoft: '#fee2e2',
  badBand: '#fca5a5',
  ruby: '#92400e',
};

export function statusChipSx(status: string): { bgcolor: string; color: string } {
  if (status === 'ok') {
    return { bgcolor: tokens.okSoft, color: tokens.ok };
  }
  if (status === 'error') {
    return { bgcolor: tokens.badSoft, color: tokens.bad };
  }
  return { bgcolor: tokens.soft, color: tokens.muted };
}

const theme = createTheme({
  palette: {
    primary: { main: tokens.fg, contrastText: '#ffffff' },
    secondary: { main: tokens.ruby, contrastText: '#ffffff' },
    error: { main: tokens.bad },
    success: { main: tokens.ok },
    warning: { main: '#a16207' },
    text: { primary: tokens.fg, secondary: tokens.muted },
    divider: tokens.border,
    background: { default: '#ffffff', paper: '#ffffff' },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: 'Geist, ui-sans-serif, system-ui, sans-serif',
    h1: { fontWeight: 600, letterSpacing: '-0.03em', fontSize: '1.375rem' },
    h4: { fontWeight: 600, letterSpacing: '-0.03em', fontSize: '1.375rem' },
    h5: { fontWeight: 600, fontSize: '1.125rem' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#ffffff', color: tokens.fg },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { boxShadow: 'none', borderRadius: 8, '&:hover': { boxShadow: 'none' } },
        containedPrimary: { backgroundColor: tokens.fg, '&:hover': { backgroundColor: '#0a0a0a' } },
        outlined: { borderColor: tokens.border },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
          border: `1px solid ${tokens.border}`,
          borderRadius: 10,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: 'none', border: `1px solid ${tokens.border}`, borderRadius: 10 },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.border },
        head: { fontWeight: 500, fontSize: '0.75rem', color: tokens.muted, backgroundColor: '#fff' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 500 },
        outlined: { borderColor: tokens.border },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: '#fff' },
        // The notch width otherwise animates open after the label has already
        // moved onto the border, so the outline cuts through the name until
        // the next keystroke repaints the field.
        notchedOutline: {
          '& legend': { transition: 'none' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { transition: 'none' },
        outlined: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#fff',
            paddingLeft: 4,
            paddingRight: 4,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        listbox: { padding: 4, fontSize: 13 },
        option: { minHeight: 36, borderRadius: 6 },
        paper: { border: `1px solid ${tokens.border}` },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          maxHeight: '92vh',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          padding: '14px 20px 8px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 20px 12px',
          // MUI zeroes the top padding after a DialogTitle. The floating label
          // of the first form row extends ~9px above its input and would be
          // clipped by the scroll box, so keep a little headroom.
          '.MuiDialogTitle-root + &': { paddingTop: 12 },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 20px 14px',
          gap: 8,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(23,23,23,0.08)',
        },
        list: { padding: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { borderRadius: 6, fontSize: 13, minHeight: 36 },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(23,23,23,0.08)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
  },
});

export default theme;
